import { Controller } from "@hotwired/stimulus"
import * as pdfjsLib from "pdfjs-dist"

// Worker is copied to public/ by the build script to stay in sync with the library version.
// Cache-bust with the library version to prevent stale worker mismatches.
const PDFJS_VERSION = pdfjsLib.version
pdfjsLib.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs?v=${PDFJS_VERSION}`

export default class extends Controller {
  static values = {
    url: String
  }

  static targets = ["canvas", "container", "pageInfo", "prevBtn", "nextBtn"]

  connect() {
    this.pdfDoc = null
    this.currentPage = 1
    this.totalPages = 0
    this.rendering = false

    // Listen for window resize to re-render
    this.resizeHandler = this.handleResize.bind(this)
    window.addEventListener("resize", this.resizeHandler)

    if (this.urlValue) {
      // Small delay to allow flexbox layout to settle
      requestAnimationFrame(() => {
        this.loadDocument()
      })
    }
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeHandler)
  }

  async loadDocument(url) {
    try {
      // Reset rendering state so a new render isn't blocked by a prior in-flight render
      this.rendering = false

      const docUrl = url || this.urlValue
      const loadingTask = pdfjsLib.getDocument(docUrl)
      this.pdfDoc = await loadingTask.promise
      this.totalPages = this.pdfDoc.numPages
      this.currentPage = 1
      this.updatePageInfo()
      await this.renderPage(this.currentPage)
    } catch (error) {
      console.error("Error loading PDF:", error)
    }
  }

  async switchDocument(url) {
    await this.loadDocument(url)
  }

  async renderPage(pageNum) {
    if (this.rendering || !this.pdfDoc) return
    this.rendering = true

    try {
      const page = await this.pdfDoc.getPage(pageNum)

      // Calculate scale to fit container
      const scale = this.calculateScale(page)
      const viewport = page.getViewport({ scale })

      const canvas = this.canvasTarget
      const context = canvas.getContext("2d")

      canvas.height = viewport.height
      canvas.width = viewport.width

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      }

      await page.render(renderContext).promise

      this.rendering = false
      this.currentPage = pageNum
      this.updatePageInfo()

      // Dispatch event for field placement controller
      this.dispatch("pageRendered", {
        detail: {
          page: pageNum,
          width: canvas.width,
          height: canvas.height
        }
      })
    } catch (error) {
      console.error("Error rendering page:", error)
      this.rendering = false
    }
  }

  calculateScale(page) {
    if (!this.hasContainerTarget) return 1.5

    const container = this.containerTarget
    // Get available space (subtract padding for shadows and breathing room)
    const availableWidth = container.clientWidth - 48
    const availableHeight = container.clientHeight - 24

    // Get the page dimensions at scale 1
    const viewport = page.getViewport({ scale: 1 })
    const pageWidth = viewport.width
    const pageHeight = viewport.height

    const scaleX = availableWidth / pageWidth

    // On mobile (below Bootstrap md breakpoint), scale to fit width only
    // so the PDF renders at full size and the page scrolls naturally.
    if (window.matchMedia("(max-width: 767.98px)").matches) {
      return Math.min(scaleX, 2.0)
    }

    // Desktop: fit both width and height within the fixed container
    const scaleY = availableHeight / pageHeight
    return Math.min(scaleX, scaleY, 2.0)
  }

  handleResize() {
    // Debounce resize handling
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
    }
    this.resizeTimeout = setTimeout(() => {
      if (this.pdfDoc && !this.rendering) {
        this.renderPage(this.currentPage)
      }
    }, 150)
  }

  previousPage() {
    if (this.currentPage <= 1) return
    this.renderPage(this.currentPage - 1)
  }

  nextPage() {
    if (this.currentPage >= this.totalPages) return
    this.renderPage(this.currentPage + 1)
  }

  goToPage(pageNum) {
    if (pageNum < 1 || pageNum > this.totalPages) return
    if (pageNum === this.currentPage) return
    this.renderPage(pageNum)
  }

  updatePageInfo() {
    if (this.hasPageInfoTarget) {
      this.pageInfoTarget.textContent = `Page ${this.currentPage} of ${this.totalPages}`
    }
    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.disabled = this.currentPage <= 1
    }
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.disabled = this.currentPage >= this.totalPages
    }
  }

  // Getter for current page (used by field placement controller)
  get page() {
    return this.currentPage
  }
}
